/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import isNil from 'lodash/isNil';
import { SecurityHelper } from 'sarsaparilla';
import {
    CHALLENGE_EAST,
    CHALLENGE_WEST,
    MOBILE_RECAPTCHA_TYPES,
    RECAPTCHA_CHALLENGE_FAILED,
    RECAPTCHA_CHALLENGE_REQUIRED,
} from '../constants/challenge';

// Builds the `gate_a` payload the captcha middleware reads: `value` is the
// token, `terminal` selects the version ("east" = V3, "west" = V2, or the
// native ENTERPRISE_IOS / ENTERPRISE_ANDROID type) and
// `description` is the action name forwarded to Google as the expected action.
// `terminal` must always be emitted - the middleware defaults any empty or
// unrecognized value to V3, which would verify a V2 token against the V3 sitekey.
const buildGate = (source, terminal) => {
    const gate = { terminal };

    const value = source?.token ?? '';
    if (!isEqual(value, '')) {
        gate.value = value;
    }

    const description = source?.action ?? '';
    if (!isEqual(description, '')) {
        gate.description = description;
    }

    const error = source?.error ?? '';
    if (isEqual(error, '') || isNil(error)) {
        gate.success = true;
    }

    return gate;
};

// Native token the mobile app injects via cookies, or null outside the app.
export const getMobileRecaptcha = (action) => {
    const token = SecurityHelper.getMobileNativeRecaptchaToken();
    const type = SecurityHelper.getMobileNativeRecaptchaType();
    if (!token || !MOBILE_RECAPTCHA_TYPES.includes(type)) {
        return null;
    }
    return { token, type, action };
};

export const deltaParams = (delta) => {
    if (isEmpty(delta)) {
        return {};
    }
    if (!isEmpty(delta.mobile)) {
        return buildGate(delta.mobile, delta.mobile.type);
    }
    if (!isEmpty(delta.v3)) {
        return buildGate(delta.v3, CHALLENGE_EAST);
    }
    if (!isEmpty(delta.v2)) {
        return buildGate(delta.v2, CHALLENGE_WEST);
    }
    return {};
};

// Normalizes the shapes a rejected reservation can take - bare error string,
// axios error, unwrapped response body, or `{ errors: [] }` envelope - into the
// server's `error` message.
export const extractRecaptchaMessage = (error) => {
    if (typeof error === 'string') {
        return error;
    }
    const message =
        error?.response?.data?.error ??
        error?.error ??
        error?.errors?.[0] ??
        error?.message ??
        '';
    return typeof message === 'string' ? message : '';
};

// The v3 assessment was inconclusive; the client should retry with a v2 checkbox
// token. Matched as a substring so a wrapping prefix doesn't defeat detection.
export const isRecaptchaChallenge = (error) =>
    extractRecaptchaMessage(error).includes(RECAPTCHA_CHALLENGE_REQUIRED);

// The v2 token was also refused, so there is no further fallback to offer.
export const isRecaptchaChallengeFailed = (error) =>
    extractRecaptchaMessage(error).includes(RECAPTCHA_CHALLENGE_FAILED);

// Either signal. These are protocol messages, not user-facing copy, so callers
// must keep them out of error notifications.
export const isRecaptchaChallengeMessage = (error) =>
    isRecaptchaChallenge(error) || isRecaptchaChallengeFailed(error);
